import { useEffect, useRef } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import { OverlayScrollArea } from '@odo/shared-ui';
import type { StepMeta } from './clinicalSteps';
import { WizardStepTab } from './WizardStepTab';

// Matches the strip's px-5, so a tab scrolled into view keeps the breathing room it has at rest.
const TAB_SCROLL_MARGIN_PX = 20;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

interface WizardStepTabsProps {
    steps: StepMeta[];
    stepIndex: number;
    onSelect: (index: number) => void;
    /** Attachment upload in-flight or failed — blocks jumps past that step. */
    isAttachmentBlocked: boolean;
    attachmentBlockedHint?: string;
}

// The first reveal (mount or resume) jumps; later ones glide unless the user asked for less motion.
function revealBehavior(isFirstReveal: boolean): ScrollBehavior {
    if (isFirstReveal || window.matchMedia(REDUCED_MOTION_QUERY).matches) {
        return 'auto';
    }
    return 'smooth';
}

// Scrolls only the strip, never the page, unlike scrollIntoView.
function revealTabInStrip(strip: HTMLElement, tab: HTMLElement, behavior: ScrollBehavior): void {
    const stripBounds = strip.getBoundingClientRect();
    const tabBounds = tab.getBoundingClientRect();
    const hiddenLeftPx = stripBounds.left + TAB_SCROLL_MARGIN_PX - tabBounds.left;
    const hiddenRightPx = tabBounds.right + TAB_SCROLL_MARGIN_PX - stripBounds.right;
    if (hiddenLeftPx > 0) {
        strip.scrollBy({ left: -hiddenLeftPx, behavior });
    } else if (hiddenRightPx > 0) {
        strip.scrollBy({ left: hiddenRightPx, behavior });
    }
}

interface ActiveTabReveal {
    stripRef: MutableRefObject<HTMLDivElement | null>;
    activeTabRef: RefObject<HTMLButtonElement>;
}

function useRevealActiveTab(activeStepId: StepMeta['id'] | undefined): ActiveTabReveal {
    const stripRef = useRef<HTMLDivElement | null>(null);
    const activeTabRef = useRef<HTMLButtonElement>(null);
    const hasRevealedRef = useRef(false);

    useEffect(() => {
        const strip = stripRef.current;
        const activeTab = activeTabRef.current;
        if (strip && activeTab) {
            revealTabInStrip(strip, activeTab, revealBehavior(!hasRevealedRef.current));
        }
        hasRevealedRef.current = true;
    }, [activeStepId]);

    return { stripRef, activeTabRef };
}

// Pill step tabs; no forward jumps past the attachment step while its upload is pending or failed.
export function WizardStepTabs({
    steps,
    stepIndex,
    onSelect,
    isAttachmentBlocked,
    attachmentBlockedHint
}: WizardStepTabsProps) {
    const attachmentStepIndex = steps.findIndex((s) => s.id === 'attachment');
    const { stripRef, activeTabRef } = useRevealActiveTab(steps[stepIndex]?.id);

    return (
        <OverlayScrollArea
            axis="x"
            wheelToHorizontal
            scrollRef={stripRef}
            className="border-b border-gray-200 flex-shrink-0"
            contentClassName="py-4"
            innerClassName="flex items-center justify-center gap-2 px-5"
        >
            {steps.map((step, index) => {
                const isActive = index === stepIndex;
                const jumpBlocked = isAttachmentBlocked && index > attachmentStepIndex;
                return (
                    <WizardStepTab
                        key={step.id}
                        step={step}
                        index={index}
                        isActive={isActive}
                        isPast={index < stepIndex}
                        jumpBlocked={jumpBlocked}
                        jumpBlockedHint={jumpBlocked ? attachmentBlockedHint : undefined}
                        hasConnector={index < steps.length - 1}
                        onSelect={onSelect}
                        tabRef={isActive ? activeTabRef : undefined}
                    />
                );
            })}
        </OverlayScrollArea>
    );
}
